/**
 * Environment-gated data-testid utility.
 *
 * Returns the testId string only in dev/qa environments.
 * In production (app.rubidex.ai), returns undefined so the attribute is not rendered.
 *
 * Environments where testIds ARE rendered:
 *   - localhost (local development)
 *   - dev.rubidex.ai
 *   - qa.rubidex.ai
 *   - Any Vite dev mode (import.meta.env.DEV)
 *
 * Environments where testIds are NOT rendered:
 *   - app.rubidex.ai (production)
 *   - Any other production-like domain
 */

const TEST_ENV_DOMAINS = ['dev.rubidex.ai', 'qa.rubidex.ai'];

/**
 * Checks if the current environment should render data-testid attributes.
 */
export function shouldRenderTestIds(): boolean {
  // Always render in Vite dev mode
  if (import.meta.env.DEV) return true;

  // Check if we're on a known test environment domain
  const currentHost = window.location.hostname;
  return TEST_ENV_DOMAINS.includes(currentHost);
}

/**
 * Returns the data-testid value if in a test environment, undefined otherwise.
 *
 * Usage in components:
 *   <input data-testid={getTestId(authTestIds.login.form.emailInput)} />
 *
 * @param testId - The test identifier from a centralized testIds object
 * @returns The testId string in dev/qa, undefined in production
 */
export function getTestId(testId: string | undefined): string | undefined {
  return testId && shouldRenderTestIds() ? testId : undefined;
}
